import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import { styled } from '@mui/material/styles';
import { Button } from '../../Buttons/Button';
import { Rating } from '../../Rating';
import { RatingLabelContainer } from '../../Rating/RatingLabelContainer';
import { Typography } from '../../Typography';

export interface WebchatRatingCardProps {
  /**
   * The message text prompt for the rating
   */
  text: string;
  /**
   * Maximum rating value (default: 5)
   */
  max?: number;
  /**
   * Current rating value
   */
  value?: number;
  /**
   * Callback when rating changes
   */
  onChange?: (value: number | null) => void;
  /**
   * Callback when submit button is clicked
   */
  onSubmit?: (value: number) => void;
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Custom labels for each rating value
   */
  labels?: Record<number, string>;
  /**
   * Whether the rating is disabled
   */
  disabled?: boolean;
  /**
   * Whether the rating has been submitted (read-only mode)
   */
  submitted?: boolean;
  /**
   * Avatar URL for bot
   */
  avatarUrl?: string;
  /**
   * Avatar initials for bot
   */
  avatarInitials?: string;
}

const MessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(1),
}));

const Avatar = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[300],
  flexShrink: 0,
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  overflow: 'hidden',
}));

const ActivityCard = styled(Card)(() => ({
  maxWidth: 480,
  minWidth: 250,
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
}));

const MessageText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

const RatingContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
}));

/**
 * WebchatRatingCard displays a rating prompt with interactive star rating.
 * This component is designed to be a standalone, reusable rating card
 * that can be integrated into various chat interfaces.
 */
export const WebchatRatingCard: React.FC<WebchatRatingCardProps> = ({
  text,
  max = 5,
  value: controlledValue,
  onChange,
  onSubmit,
  allowSkip = true,
  skipText = 'Skip',
  submitText = 'Submit',
  onSkip,
  labels,
  disabled = false,
  submitted = false,
  avatarUrl,
  avatarInitials = 'B',
}) => {
  const [internalValue, setInternalValue] = React.useState(controlledValue || 0);
  const [hover, setHover] = React.useState(0);

  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = (_event: React.SyntheticEvent, newValue: number | null) => {
    if (newValue) {
      setInternalValue(newValue);
      onChange?.(newValue);
    }
  };

  const handleHoverChange = (_event: React.SyntheticEvent, newValue: number) => {
    setHover(newValue || 0);
  };

  const handleSubmit = () => {
    if (value > 0) {
      onSubmit?.(value);
    }
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const defaultLabels: Record<number, string> = labels || {
    1: 'Poor',
    2: 'Fair',
    3: 'Good',
    4: 'Very Good',
    5: 'Excellent',
  };

  return (
    <MessageContainer>
      <Avatar>
        {avatarUrl ? (
          <img src={avatarUrl} alt="bot avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span>{avatarInitials}</span>
        )}
      </Avatar>
      <ActivityCard>
        <CardContent>
          <MessageText>{text}</MessageText>
          <RatingContainer>
            <Rating
              name="rating"
              value={value}
              max={max}
              size="large"
              onChange={handleChange}
              onChangeActive={handleHoverChange}
              readOnly={submitted}
              disabled={disabled}
              isLabelHidden={true}
            />
            <Box sx={{ height: '40px' }}>
              <RatingLabelContainer labels={defaultLabels} hover={value || hover} size="large" />
            </Box>
          </RatingContainer>
        </CardContent>
        {!submitted && (
          <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
            {allowSkip && (
              <Button size="small" variant="text" onClick={handleSkip} disabled={disabled}>
                {skipText}
              </Button>
            )}
            <Button size="small" variant="contained" onClick={handleSubmit} disabled={value < 1 || disabled}>
              {submitText}
            </Button>
          </CardActions>
        )}
      </ActivityCard>
    </MessageContainer>
  );
};
